import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Attach the selected establishment (ONE GROUP) to every request. Server-side
// membership is still enforced — this header is only a context hint.
api.interceptors.request.use((config) => {
  try {
    const est = window.localStorage.getItem("st.establishment");
    if (est) config.headers["X-Establishment-Id"] = est;
  } catch { /* ignore */ }
  // Let the browser set multipart/form-data (with boundary) for uploads —
  // the instance default of application/json otherwise breaks FormData posts.
  if (typeof FormData !== "undefined" && config.data instanceof FormData) {
    if (config.headers) delete config.headers["Content-Type"];
  }
  return config;
});

// Response interceptor for auth + preview-mode signalling
api.interceptors.response.use(
  (r) => {
    // Server may return 200 with preview_mode_blocked when the operator
    // is impersonating a customer and attempts a write.  Surface a toast
    // so the operator sees why nothing happened, and reject downstream.
    if (r?.data?.preview_mode_blocked) {
      toast.info("PREVIEW MODE — action would have started " +
                    (r.data.attempted?.path || "a workflow") +
                    ". Exit preview to run for real.",
                    { duration: 4000 });
      return Promise.reject({ preview_mode_blocked: true, response: r });
    }
    return r;
  },
  async (err) => {
    // Credential endpoints never refresh-and-replay: a wrong password must count once toward lockout, not twice.
    const noReplay = /\/auth\/(login|refresh|tenant-hint|logout)$/.test(err.config?.url || "");
    if (err.response?.status === 401 && err.config && !err.config._retry && !noReplay) {
      err.config._retry = true;
      try {
        await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
        return api(err.config);
      } catch {
        // ignore
      }
    }
    // Normalize FastAPI validation errors: `detail` can be an ARRAY of
    // objects, which crashes React when rendered directly in a toast
    // ("Objects are not valid as a React child").  Always hand call
    // sites a plain string.
    const d = err.response?.data;
    if (err.response?.status === 403 && typeof d?.detail === "string" && d.detail.startsWith("Your role is view-only")) {
      toast.error("View-only access — you can view and download, but not change records.", { id: "view-only-block" });
    }
    if (d && typeof d.detail !== "undefined" && typeof d.detail !== "string") {
      d._rawDetail = d.detail;
      try { d.detail = formatApiError(d.detail); } catch { d.detail = "Request failed — please check the form values."; }
    }
    return Promise.reject(err);
  }
);

export function formatApiError(detail) {
  const raw = _rawDetail(detail);
  if (!raw) return "Something went wrong. Please try again.";
  const s = String(raw);
  // Customer-friendly rewrites for common backend messages
  const map = [
    [/^Not authorized$/i,                    "You don't have access to this action."],
    [/^Suntronix only$/i,                    "This action can only be performed by the Suntronix team."],
    [/^Super admin only$/i,                  "This action requires a super-admin."],
    [/not found$/i,                          "We couldn't find what you were looking for."],
    [/Invalid.+entity/i,                     "We couldn't find this record."],
    [/^403 Unauthorized$/i,                  "You don't have access to this information."],
    [/already registered/i,                  "An account with that email already exists."],
    [/already reserved/i,                    "This device is already reserved for that window."],
    [/still holds .* asset/i,                "This person still holds devices. Please recover them before deleting."],
    [/legal hold/i,                          "This device is under legal hold and cannot be deleted."],
    [/assigned to an employee/i,             "This device is currently assigned. Please recover it first."],
    [/asset\(s\) still inside this warehouse/i, "This warehouse still holds inventory. Please move devices out first."],
  ];
  for (const [pattern, replacement] of map) {
    if (pattern.test(s)) return replacement;
  }
  return s;
}

// Turn a FastAPI/Pydantic validation error field path (`loc`) into a
// human field name, e.g. ["body","po_date"] -> "Po date".
function _fieldFromLoc(loc) {
  if (!Array.isArray(loc)) return null;
  const parts = loc.filter((p) => typeof p === "string" && p !== "body" && p !== "query" && p !== "path");
  const name = parts[parts.length - 1];
  if (!name) return null;
  const words = String(name).replace(/[_.]+/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : null;
}

function _rawDetail(detail) {
  if (detail == null) return null;
  if (typeof detail === "string") return detail;
  if (typeof detail === "number" || typeof detail === "boolean") return String(detail);
  if (Array.isArray(detail))
    return detail.map(_rawDetail).filter(Boolean).join(" · ");
  if (typeof detail === "object") {
    // Pydantic validation error shape: {loc, msg, type, ...}. Keep ONLY
    // the human message + the field name — strip all technical noise
    // (loc/type/input/url/ctx) that confuses end users.
    if (typeof detail.msg === "string" && detail.msg.trim() && "loc" in detail) {
      const field = _fieldFromLoc(detail.loc);
      return field ? `${field}: ${detail.msg}` : detail.msg;
    }
    for (const key of ["msg", "message", "error", "detail", "reason"]) {
      if (typeof detail[key] === "string" && detail[key].trim()) {
        const extras = _objectExtras(detail, key);
        return extras ? `${detail[key]} (${extras})` : detail[key];
      }
    }
    const extras = _objectExtras(detail, null);
    if (extras) return extras;
    return null;
  }
  return String(detail);
}

// Technical keys that must never leak into a user-facing message.
const _NOISE_KEYS = new Set(["loc", "type", "input", "url", "ctx"]);

function _objectExtras(obj, skipKey) {
  const parts = [];
  for (const [k, v] of Object.entries(obj)) {
    if (k === skipKey || _NOISE_KEYS.has(k) || v == null) continue;
    if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") {
      parts.push(`${k}: ${v}`);
    } else if (Array.isArray(v) && v.length) {
      const items = v.map(_rawDetail).filter(Boolean).slice(0, 3).join("; ");
      if (items) parts.push(`${k}: ${items}`);
    }
  }
  return parts.slice(0, 3).join(", ");
}

export default api;
